import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useAuth } from '@/contexts/AuthContext';
import { 
  Calendar, Plus, MapPin, Video, Users, Clock, Check, Trash2, Loader2, ExternalLink
} from 'lucide-react';
import { getErrorMessage } from '../utils/errorHandler';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventsTab = ({ communityId, isAdmin }) => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [rsvping, setRsvping] = useState(null);
  
  // New event form
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    location: '',
    is_online: false,
    meeting_link: '',
    max_attendees: null
  });

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API}/communities/${communityId}/events?include_past=true`,
        { withCredentials: true }
      );
      setEvents(response.data);
    } catch (error) {
      console.error('Failed to fetch events:', error);
    } finally {
      setLoading(false);
    }
  }, [communityId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleCreateEvent = async () => {
    if (!newEvent.title.trim()) {
      toast.error('Please enter an event title');
      return;
    }
    
    if (!newEvent.event_date || !newEvent.event_time) {
      toast.error('Please select a date and time');
      return;
    }
    
    try {
      const eventDateTime = new Date(`${newEvent.event_date}T${newEvent.event_time}`).toISOString();
      
      await axios.post(
        `${API}/communities/${communityId}/events`,
        {
          title: newEvent.title,
          description: newEvent.description || null,
          event_date: eventDateTime,
          location: newEvent.is_online ? null : newEvent.location,
          is_online: newEvent.is_online,
          meeting_link: newEvent.is_online ? newEvent.meeting_link : null,
          max_attendees: newEvent.max_attendees
        },
        { withCredentials: true }
      );
      
      toast.success('Event created!');
      setCreateDialogOpen(false);
      setNewEvent({
        title: '',
        description: '',
        event_date: '',
        event_time: '',
        location: '',
        is_online: false,
        meeting_link: '',
        max_attendees: null
      });
      fetchEvents();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to create event'));
    }
  };

  const handleRsvp = async (eventId) => {
    setRsvping(eventId);
    try {
      const response = await axios.post(
        `${API}/communities/${communityId}/events/${eventId}/rsvp`,
        {},
        { withCredentials: true }
      );
      toast.success(response.data.is_attending ? 'You\'re attending!' : 'RSVP cancelled');
      fetchEvents();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to RSVP'));
    } finally {
      setRsvping(null);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) return;
    
    try {
      await axios.delete(
        `${API}/communities/${communityId}/events/${eventId}`,
        { withCredentials: true }
      );
      toast.success('Event deleted');
      fetchEvents();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete event'));
    }
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatEventTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const isEventPast = (dateString) => {
    return new Date(dateString) < new Date();
  };

  // Get today's date for min attribute
  const today = new Date().toISOString().split('T')[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with Create Button */}
      {isAdmin && (
        <div className="flex justify-end">
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="create-event-btn">
                <Plus className="w-4 h-4" />
                Create Event
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Event</DialogTitle>
                <DialogDescription>Schedule an event for your community</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                <div>
                  <Label>Event Title</Label>
                  <Input
                    placeholder="Sunday Worship Service"
                    value={newEvent.title}
                    onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                    className="mt-1"
                    data-testid="event-title-input"
                  />
                </div>
                
                <div>
                  <Label>Description (optional)</Label>
                  <Textarea
                    placeholder="Tell people what this event is about..."
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    className="mt-1"
                    rows={3}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date</Label>
                    <Input
                      type="date"
                      min={today}
                      value={newEvent.event_date}
                      onChange={(e) => setNewEvent({ ...newEvent, event_date: e.target.value })}
                      className="mt-1"
                      data-testid="event-date-input"
                    />
                  </div>
                  <div>
                    <Label>Time</Label>
                    <Input
                      type="time"
                      value={newEvent.event_time}
                      onChange={(e) => setNewEvent({ ...newEvent, event_time: e.target.value })}
                      className="mt-1"
                      data-testid="event-time-input"
                    />
                  </div>
                </div>
                
                <div className="flex items-center justify-between">
                  <Label>Online Event</Label>
                  <Switch
                    checked={newEvent.is_online}
                    onCheckedChange={(checked) => setNewEvent({ ...newEvent, is_online: checked })}
                  />
                </div>
                
                {newEvent.is_online ? (
                  <div>
                    <Label>Meeting Link</Label>
                    <Input
                      placeholder="https://zoom.us/j/..."
                      value={newEvent.meeting_link}
                      onChange={(e) => setNewEvent({ ...newEvent, meeting_link: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                ) : (
                  <div>
                    <Label>Location</Label>
                    <Input
                      placeholder="Main Sanctuary"
                      value={newEvent.location}
                      onChange={(e) => setNewEvent({ ...newEvent, location: e.target.value })}
                      className="mt-1"
                    />
                  </div>
                )}
                
                <div>
                  <Label>Max Attendees (optional)</Label>
                  <Input
                    type="number"
                    placeholder="Unlimited"
                    value={newEvent.max_attendees || ''}
                    onChange={(e) => setNewEvent({ 
                      ...newEvent, 
                      max_attendees: e.target.value ? parseInt(e.target.value) : null 
                    })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateEvent} data-testid="submit-event-btn">Create Event</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* Events List */}
      {events.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No upcoming events</p>
          <p className="text-sm">Create an event to bring your community together</p>
        </div>
      ) : (
        <ScrollArea className="h-[400px] pr-4">
          <div className="space-y-4">
            <AnimatePresence>
              {events.map((event) => (
                <EventCard
                  key={event.event_id}
                  event={event}
                  userId={user?.user_id}
                  isAdmin={isAdmin}
                  isPast={isEventPast(event.event_date)}
                  rsvping={rsvping === event.event_id}
                  onRsvp={() => handleRsvp(event.event_id)}
                  onDelete={() => handleDeleteEvent(event.event_id)}
                  formatDate={formatEventDate}
                  formatTime={formatEventTime}
                />
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>
      )}
    </div>
  );
};

const EventCard = ({ 
  event, userId, isAdmin, isPast, rsvping, 
  onRsvp, onDelete, formatDate, formatTime 
}) => {
  const canEdit = isAdmin || event.created_by === userId;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
    >
      <Card className={isPast ? 'opacity-60' : ''}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                {event.title}
                {isPast && <Badge variant="secondary">Past</Badge>}
              </CardTitle>
              <CardDescription className="mt-1">
                {event.description}
              </CardDescription>
            </div>
            {canEdit && !isPast && (
              <Button variant="ghost" size="sm" onClick={onDelete}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {/* Date & Time */}
            <div className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <span>{formatDate(event.event_date)}</span>
              <span className="text-muted-foreground">at</span>
              <Clock className="w-4 h-4 text-muted-foreground" />
              <span>{formatTime(event.event_date)}</span>
            </div>
            
            {/* Location */}
            <div className="flex items-center gap-2 text-sm">
              {event.is_online ? (
                <>
                  <Video className="w-4 h-4 text-muted-foreground" />
                  <span>Online Event</span>
                  {event.meeting_link && (
                    <a 
                      href={event.meeting_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-primary hover:underline flex items-center gap-1"
                    >
                      Join <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </>
              ) : (
                <>
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{event.location || 'Location TBD'}</span>
                </>
              )}
            </div>
            
            {/* Attendees */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span>
                  {event.attendee_count} attending
                  {event.max_attendees && ` / ${event.max_attendees} max`}
                </span>
              </div>
              
              {!isPast && (
                <Button
                  variant={event.is_attending ? "secondary" : "default"}
                  size="sm"
                  onClick={onRsvp}
                  disabled={rsvping || event.is_full}
                  data-testid={`rsvp-btn-${event.event_id}`}
                >
                  {rsvping ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : event.is_attending ? (
                    <>
                      <Check className="w-4 h-4 mr-1" />
                      Attending
                    </>
                  ) : event.is_full ? (
                    'Full'
                  ) : (
                    'RSVP'
                  )}
                </Button>
              )}
            </div>
            
            {/* Created by */}
            <p className="text-xs text-muted-foreground">
              Created by {event.creator_name}
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default EventsTab;
